import type { ApiSchemas } from '@rental/contracts/client'
import { businessDateOf, type DeliveryLeg, deliveryScopeEnd } from '@rental/domain'
import { type Access, can } from '../access'
import { romeInstant, romeNow } from '../time'

export type Trip = ApiSchemas['StaffDeliveryTrip']
export type TripRow = ApiSchemas['StaffDeliveryRow']
export type TaskRequest = ApiSchemas['DeliveryTaskRequest']
type Place = ApiSchemas['StaffPlace']
type Ferry = { name: string | null; arrivalAt: string | null }

/**
 * S9 (LOC-5, J8): the day's trips, the asker's own first (a Runner reads
 * their round before anyone else's), then by when each is due.
 */
export function orderTrips(rows: readonly TripRow[]): TripRow[] {
  return [...rows].sort(
    (a, b) =>
      Number(b.mine) - Number(a.mine) ||
      a.dueAt.localeCompare(b.dueAt) ||
      a.leg.localeCompare(b.leg) ||
      a.booking.code.localeCompare(b.booking.code),
  )
}

/**
 * Whether Today opens on the trips: the day of whoever drives the trips the
 * office gives (the Runner) is their round; the counter's is its pick-ups and
 * returns. The Owner holds both permissions, and runs the counter.
 */
export function tripsLead(access: Access): boolean {
  return can(access, 'deliveries.work') && !can(access, 'deliveries.manage')
}

/** Trips nobody drives yet and that still happen: what the office has to assign. */
export function toAssign(rows: readonly TripRow[]): TripRow[] {
  return rows.filter((row) => row.state === 'to_assign')
}

/** Where a trip goes: the booking's pick-up for a delivery, its return for a collection. */
export function tripPlace(leg: DeliveryLeg, booking: { pickup: Place; return: Place }): Place {
  return leg === 'delivery' ? booking.pickup : booking.return
}

/**
 * The ferry the trip meets: the one the office wrote on it when the customer
 * turned out to come on another, else the one the booking was made with. That
 * one is the ferry the customer arrives on, so it is a delivery's only: a
 * collection meets no arrival.
 */
export function tripFerry(trip: Trip, booking: { ferry: Ferry | null }): Ferry | null {
  const task = trip.task
  if (task && (task.ferryName !== null || task.ferryArrivalAt !== null)) {
    return { name: task.ferryName, arrivalAt: task.ferryArrivalAt }
  }
  return trip.leg === 'delivery' ? booking.ferry : null
}

/**
 * S9 (Q-62): the bookings a person who cannot open bookings may still open,
 * through a trip given to them: until the end of the day after it is due, the
 * window the server keeps (deliveryScopeEnd), read on the board's own clock.
 * Only so that no link leads to a refusal; the server decides each request again.
 */
export function openedByTrips(rows: readonly TripRow[], now: string): ReadonlySet<string> {
  const at = Date.parse(now)
  return new Set(
    rows
      .filter((row) => row.mine && at < deliveryScopeEnd(new Date(row.dueAt)).getTime())
      .map((row) => row.booking.code),
  )
}

/** What the office types on a trip, as text fields hold it. */
export interface TripForm {
  /** A person's id; '' for nobody. */
  assignedTo: string
  meetingPoint: string
  note: string
  ferryName: string
  /** Rome wall-clock, "HH:MM", on the trip's own day. */
  ferryTime: string
}

export function tripForm(trip: Trip): TripForm {
  const task = trip.task
  return {
    assignedTo: task?.assignedTo?.id ?? '',
    meetingPoint: task?.meetingPoint ?? '',
    note: task?.note ?? '',
    ferryName: task?.ferryName ?? '',
    ferryTime: task?.ferryArrivalAt ? romeNow(new Date(task.ferryArrivalAt)).time : '',
  }
}

/**
 * The whole task as the server takes it, with the version read: a blank
 * field is null, and the ferry's time is placed on the day the trip is due.
 */
export function taskRequest(
  trip: Trip,
  form: TripForm,
): { ok: true; body: TaskRequest } | { ok: false; invalid: 'ferryTime' } {
  const text = (value: string) => (value.trim() === '' ? null : value.trim())
  let ferryArrivalAt: string | null = null
  if (form.ferryTime.trim() !== '') {
    ferryArrivalAt = romeInstant(businessDateOf(new Date(trip.dueAt)), form.ferryTime.trim())
    if (ferryArrivalAt === null) return { ok: false, invalid: 'ferryTime' }
  }
  return {
    ok: true,
    body: {
      version: trip.task?.version ?? null,
      assignedTo: form.assignedTo === '' ? null : form.assignedTo,
      meetingPoint: text(form.meetingPoint),
      note: text(form.note),
      ferryName: text(form.ferryName),
      ferryArrivalAt,
    },
  }
}
